import {
	OpenAccountDataToGetApplication,
	OpenAccountDataToGetClientInfo,
	OpenAccountDataToGetInvoiceList,
	OpenAccountDataToSendCode,
	OpenAccountDataToVerifyCode,
	OpenAccountInvoice,
	OpenAccountInvoiceType,
	OpenAccountRequestData,
} from "@/feature/OpenAccount/store/interface";
import { InvoiceListItem, InvoiceListItemContent } from "@/components/List/IBInvoiceList/interface";
import { OpenAccountFormStep } from "@/feature/OpenAccount/mixins/interface";
import ObjectUtils from "@/modules/Object/ObjectUtils";
import $t from '@/plugins/i18n/i18n';
import { DateUtils } from "@/modules/DateCore/DateUtils";

export const openAccountStepsByStatus = {
	[OpenAccountInvoiceType.ACCOUNT_OPENED]: OpenAccountFormStep.DONE,
	[OpenAccountInvoiceType.DECLINED_BY_BANK]: OpenAccountFormStep.REJECTED_BY_BANK,
	[OpenAccountInvoiceType.DECLINED_BY_CUSTOMER]: OpenAccountFormStep.REJECTED_BY_CLIENT,
	[OpenAccountInvoiceType.OPEN]: OpenAccountFormStep.ACCEPTED_BY_BANK,
};

export const openAccountIconSrcByStatus = {
	[OpenAccountInvoiceType.ACCOUNT_OPENED]: '/img/icons/status-success.svg',
	[OpenAccountInvoiceType.DECLINED_BY_BANK]: '/img/icons/status-error.svg',
	[OpenAccountInvoiceType.DECLINED_BY_CUSTOMER]: '/img/icons/status-error.svg',
	[OpenAccountInvoiceType.OPEN]: '/img/icons/clock.svg',
};

const getListItemsByOpenAccountInvoice = (
	invoice: OpenAccountInvoice | null
): InvoiceListItemContent[] => [
	{
		title: `${$t.t('openAccount.organizationName')}:`,
		text: invoice?.legalEntityName
	},
	{
		title: `${$t.t('openAccount.initiatorPhone')}:`,
		text: invoice?.phoneNumber
	},
	{
		title: `${$t.t('openAccount.innOrPinfl')}:`,
		text: invoice?.inn || invoice?.pinfl
	},
	{
		title: `${$t.t('openAccount.applicationNumber')}:`,
		text: invoice?.applicationNumber
	},
	{
		title: `${$t.t('openAccount.applicationDate')}:`,
		text: invoice?.applicationDateTime
			? DateUtils.getFormattedTimeDate(
				new Date(invoice.applicationDateTime)
			)
			: ''
	},
	{
		title: `${$t.t('openAccount.selectedTariff')}:`,
		text: invoice?.tariffId
	},
	{
		title: `${$t.t('openAccount.accountTypeTitle')}:`,
		text: invoice?.accountType
			? $t.t('openAccount.accountType.' + invoice.accountType) as string
			: ''
	},
	{
		title: `${$t.t('openAccount.employeesCount')}:`,
		text: invoice?.countEmployeesNeedsAccessRb?.toString()
	},
	{
		title: `${$t.t('openAccount.branchNumber')}:`,
		text: invoice?.mfo
	},
	{
		title: `${$t.t('openAccount.assignedEmployee')}:`,
		text: invoice?.underwriterName
	},
	{
		title: `${$t.t('openAccount.meetingAddress')}:`,
		text: invoice?.appointmentAddress
	},
	{
		title: `${$t.t('openAccount.bankEmployeePhone')}:`,
		text: invoice?.underwriterPhoneNumber
	},
	{
		title: `${$t.t('openAccount.meetingDate')}:`,
		text: invoice?.appointmentDateTime
			? DateUtils.getFormattedTimeDate(
				new Date(invoice.appointmentDateTime)
			)
			: ''
	},
].filter(item => !!item.text);

export const mapOpenAccountInvoice = (
	invoice: OpenAccountInvoice | null
): InvoiceListItem => ({
	content: {
		title: `№ ${invoice?.applicationNumber || ''}`,
		text: invoice?.legalEntityName || '',
	},
	status: {
		title: $t.t(`openAccount.status.${invoice?.applicationStatus}`) as string,
		text: invoice?.applicationDateTime
			? DateUtils.getFormattedTimeDate(
				new Date(invoice.applicationDateTime)
			)
			: '',
		is: (invoice?.applicationStatus
			&& openAccountStepsByStatus[invoice.applicationStatus])
			|| OpenAccountFormStep.COMPANY_INFO,
		iconSrc: (invoice?.applicationStatus
			&& openAccountIconSrcByStatus[invoice.applicationStatus])
			|| openAccountIconSrcByStatus[OpenAccountInvoiceType.OPEN]
	},
	list: getListItemsByOpenAccountInvoice(invoice),
});

export const mapOpenAccountInvoices = (
	list: OpenAccountInvoice[]
): InvoiceListItem[] => list.map(mapOpenAccountInvoice);

export const getDataToSendCode = (
	requestData: OpenAccountRequestData
): OpenAccountDataToSendCode => ObjectUtils.excludeEmpty({
	phoneNumber: requestData?.phoneNumber,
	repeat: requestData?.repeat,
}, [ 'repeat' ]) as OpenAccountDataToSendCode;

export const getDataToVerifyCode = (
	requestData: OpenAccountRequestData
): OpenAccountDataToVerifyCode => ObjectUtils.excludeEmpty({
	phoneNumber: requestData?.phoneNumber,
	smsCode: requestData?.smsCode,
}) as OpenAccountDataToVerifyCode;

export const getDataToGetInvoiceList = (
	requestData: OpenAccountRequestData
): OpenAccountDataToGetInvoiceList => ObjectUtils.excludeEmpty({
	inn: requestData?.inn,
	pinfl: requestData?.pinfl,
	phoneNumber: requestData?.phoneNumber,
}) as OpenAccountDataToGetInvoiceList;

export const getDataToGetInvoice = (
	invoice: OpenAccountInvoice
): OpenAccountDataToGetApplication => ObjectUtils.excludeEmpty({
	applicationId: invoice?.applicationId,
}) as OpenAccountDataToGetApplication;

export const getDataToGetClientInfo = (
	requestData: OpenAccountRequestData
): OpenAccountDataToGetClientInfo => ObjectUtils.excludeEmpty({
	phoneNumber: requestData?.phoneNumber,
	inn: requestData?.inn,
	pinfl: requestData?.pinfl,
	nonClientName: requestData?.nonClientName,
}) as OpenAccountDataToGetClientInfo;